import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Pagination, PAGE_SIZE } from "@/components/Pagination";
import { toast } from "sonner";
import { ArrowsDownUp, HandCoins, Plus, Receipt, Rows, SquaresFour } from "@phosphor-icons/react";
import { AppHeader } from "@/components/AppHeader";
import { FilterBar } from "@/components/FilterBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DateField } from "@/components/DateField";
import { api, formatApiErrorDetail } from "@/lib/api";

export const INVOICE_BADGE = {
  Draft: "border-zinc-500/25 bg-zinc-500/10 text-zinc-400",
  Sent: "border-blue-500/25 bg-blue-500/10 text-blue-400",
  "Partly paid": "border-amber-500/25 bg-amber-500/10 text-amber-400",
  Paid: "border-emerald-500/25 bg-emerald-500/10 text-emerald-400",
  Void: "border-zinc-600/25 bg-zinc-700/10 text-zinc-500",
};

export const INVOICE_DOT = {
  Draft: "bg-zinc-400",
  Sent: "bg-blue-400",
  "Partly paid": "bg-amber-400",
  Paid: "bg-emerald-400",
  Void: "bg-zinc-500",
};

// The payment plan an invoice belongs to, read from its kind.
export const INVOICE_PLAN = {
  deposit: { label: "50/50 · Deposit", cls: "border-amber-500/25 bg-amber-500/10 text-amber-400" },
  balance: { label: "50/50 · Balance", cls: "border-sky-500/25 bg-sky-500/10 text-sky-300" },
  invoice: { label: "Full invoice", cls: "border-emerald-500/25 bg-emerald-500/10 text-emerald-400" },
};

export const planPill = (kind) => INVOICE_PLAN[kind] || null;

// Invoice list "Plan" filter: match a filter choice to an invoice kind.
const PLAN_KINDS = {
  Full: (k) => k === "invoice",
  "50/50": (k) => k === "deposit" || k === "balance",
  Deposit: (k) => k === "deposit",
  Balance: (k) => k === "balance",
};
export const planMatch = (plan, kind) =>
  PLAN_KINDS[plan] ? PLAN_KINDS[plan](kind) : true;

// Sort order when grouping the invoice table by plan.
const PLAN_RANK = { invoice: 0, deposit: 1, balance: 2, custom: 3 };

// Per-invoice QuickBooks sync state shown in the office list + detail.
export const QBO_TAG = {
  synced: { label: "Synced ✓", cls: "border-emerald-500/25 bg-emerald-500/10 text-emerald-400" },
  linked: { label: "Linked to existing QB", cls: "border-sky-500/30 bg-sky-500/10 text-sky-300" },
  failed: { label: "Sync failed", cls: "border-red-500/30 bg-red-500/10 text-red-400" },
  pending: { label: "Not synced", cls: "border-zinc-700 bg-zinc-800 text-zinc-500" },
};
export const qboTag = (q) => QBO_TAG[q?.status === "synced" && q?.linked_existing ? "linked" : q?.status] || QBO_TAG.pending;

export const money = (n) =>
  `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const day = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "On install";

export const isOverdue = (r) =>
  !!r.due_date &&
  !["Paid", "Void"].includes(r.status) &&
  Number(r.balance) > 0 &&
  new Date(r.due_date) < new Date(new Date().setHours(0, 0, 0, 0));

const OverdueDot = ({ id }) => (
  <span
    className="mr-1.5 inline-block h-2 w-2 rounded-full bg-red-400 align-middle"
    data-testid={`invoice-overdue-dot-${id}`}
    title="Overdue"
  />
);

const PAY_METHODS = ["Check", "Cash", "Card", "Bank transfer", "Other"];

const Stat = ({ label, value, testid }) => (
  <div
    className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"
    data-testid={testid}
  >
    <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
      {label}
    </div>
    <div className="font-mono-data mt-1 text-xl font-bold">{value}</div>
  </div>
);

export default function Invoices() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({
    statuses: [],
    outstanding: 0,
    paid: 0,
  });
  const [filter, setFilter] = useState("All");
  const [plan, setPlan] = useState("All");
  const [planSort, setPlanSort] = useState(null);
  const [q, setQ] = useState("");
  const location = useLocation();
  const projectFilter = new URLSearchParams(location.search).get("project") || "";
  const wantsNew = new URLSearchParams(location.search).get("new") === "1";
  const [open, setOpen] = useState(false);
  const [layout, setLayout] = useState(
    () => localStorage.getItem("bg_invoices_view") || "table",
  );
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState({
    customer_id: "",
    title: "",
    amount: "",
    due_date: "",
    notes: "",
  });

  // Bulk "apply one payment across several invoices" (one customer at a time).
  const [payOpen, setPayOpen] = useState(false);
  const [payCust, setPayCust] = useState("");
  const [payInvoices, setPayInvoices] = useState([]);
  const [payTotal, setPayTotal] = useState("");
  const [payMethod, setPayMethod] = useState(PAY_METHODS[0]);
  const [payRef, setPayRef] = useState("");
  const [payDate, setPayDate] = useState("");
  const [paySplit, setPaySplit] = useState("oldest"); // "oldest" | "even"
  const [paySaving, setPaySaving] = useState(false);

  const load = useCallback(async () => {
    const params = {};
    if (filter !== "All" && filter !== "Overdue") params.status = filter;
    if (q.trim()) params.q = q.trim();
    if (projectFilter) params.project_id = projectFilter;
    const { data } = await api.get("/invoices", { params });
    const all = data.invoices || [];
    let list = filter === "Overdue" ? all.filter(isOverdue) : all;
    if (plan !== "All") list = list.filter((r) => planMatch(plan, r.kind));
    setRows(list);
    setMeta({
      statuses: data.statuses || [],
      outstanding: data.outstanding || 0,
      paid: data.paid || 0,
      qboEnabled: !!data.qbo_enabled,
    });
  }, [filter, plan, q, projectFilter]);

  const displayRows = useMemo(() => {
    if (!planSort) return rows;
    const dir = planSort === "asc" ? 1 : -1;
    return [...rows].sort(
      (a, b) => dir * ((PLAN_RANK[a.kind] ?? 9) - (PLAN_RANK[b.kind] ?? 9)),
    );
  }, [rows, planSort]);

  const totalPages = Math.max(1, Math.ceil(displayRows.length / PAGE_SIZE));
  const curPage = Math.min(page, totalPages);
  const paged = displayRows.slice((curPage - 1) * PAGE_SIZE, curPage * PAGE_SIZE);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    api
      .get("/customers")
      .then(({ data }) => setCustomers(Array.isArray(data) ? data : []))
      .catch(() => setCustomers([]));
  }, []);

  useEffect(() => {
    localStorage.setItem("bg_invoices_view", layout);
  }, [layout]);

  useEffect(() => {
    if (wantsNew) setOpen(true);
  }, [wantsNew, location.key]);

  const create = async () => {
    if (!form.title.trim() || !Number(form.amount)) {
      toast.error("Add a title and an amount");
      return;
    }
    try {
      const { data } = await api.post("/invoices", {
        ...form,
        amount: Number(form.amount),
      });
      setOpen(false);
      toast.success(`${data.code} created`);
      navigate(`/invoices/${data.id}`);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not create that invoice",
      );
    }
  };

  const FILTERS = ["All", "Overdue", ...(meta.statuses || [])];

  // Fill the oldest invoices first until the entered total runs out.
  const allocateOldest = (list, total) => {
    let remaining = Math.round((Number(total) || 0) * 100) / 100;
    return list.map((inv) => {
      if (!inv.include) return { ...inv, amount: "" };
      const give = Math.max(0, Math.min(inv.balance, remaining));
      remaining = Math.round((remaining - give) * 100) / 100;
      return { ...inv, amount: give ? give.toFixed(2) : "" };
    });
  };

  // Spread the total evenly across the included invoices, capped at each balance,
  // rolling any leftover onto invoices that still have room.
  const allocateEven = (list, total) => {
    let remaining = Math.round((Number(total) || 0) * 100) / 100;
    const active = list
      .filter((i) => i.include)
      .map((i) => ({ id: i.id, balance: i.balance, amt: 0 }));
    let guard = 0;
    while (
      remaining > 0.005 &&
      active.some((a) => a.amt < a.balance - 0.005) &&
      guard++ < 50
    ) {
      const need = active.filter((a) => a.amt < a.balance - 0.005);
      const share = Math.floor((remaining / need.length) * 100) / 100;
      if (share < 0.01) break;
      for (const a of need) {
        const give = Math.min(share, Math.round((a.balance - a.amt) * 100) / 100);
        a.amt = Math.round((a.amt + give) * 100) / 100;
        remaining = Math.round((remaining - give) * 100) / 100;
      }
    }
    // leftover cents → first invoice with room
    if (remaining > 0.005) {
      for (const a of active) {
        const room = Math.round((a.balance - a.amt) * 100) / 100;
        if (room > 0.005) {
          const give = Math.min(room, remaining);
          a.amt = Math.round((a.amt + give) * 100) / 100;
          remaining = Math.round((remaining - give) * 100) / 100;
          if (remaining <= 0.005) break;
        }
      }
    }
    const map = Object.fromEntries(active.map((a) => [a.id, a.amt]));
    return list.map((inv) =>
      inv.include
        ? { ...inv, amount: map[inv.id] ? map[inv.id].toFixed(2) : "" }
        : { ...inv, amount: "" },
    );
  };

  const reallocate = (list, total, mode = paySplit) =>
    mode === "even" ? allocateEven(list, total) : allocateOldest(list, total);

  const setSplitMode = (mode) => {
    setPaySplit(mode);
    setPayInvoices((list) => reallocate(list, payTotal, mode));
  };

  const openBulkPay = () => {
    setPayCust("");
    setPayInvoices([]);
    setPayTotal("");
    setPayRef("");
    setPayDate("");
    setPaySplit("oldest");
    setPayMethod(PAY_METHODS[0]);
    setPayOpen(true);
  };

  const pickPayCustomer = async (cid) => {
    setPayCust(cid);
    setPayTotal("");
    try {
      const { data } = await api.get("/invoices", { params: { customer_id: cid } });
      const open = (data.invoices || [])
        .filter((r) => r.status !== "Void" && r.balance > 0.009)
        .sort((a, b) => {
          const ad = a.due_date || a.created_at || "";
          const bd = b.due_date || b.created_at || "";
          return ad < bd ? -1 : ad > bd ? 1 : 0;
        })
        .map((r) => ({
          id: r.id,
          code: r.code,
          title: r.title,
          due_date: r.due_date,
          balance: r.balance,
          amount: "",
          include: true,
        }));
      setPayInvoices(open);
    } catch {
      setPayInvoices([]);
    }
  };

  const setTotalAndAllocate = (val) => {
    setPayTotal(val);
    setPayInvoices((list) => reallocate(list, val));
  };

  const toggleInclude = (id) =>
    setPayInvoices((list) =>
      reallocate(
        list.map((i) => (i.id === id ? { ...i, include: !i.include } : i)),
        payTotal,
      ),
    );

  const setRowAmount = (id, val) =>
    setPayInvoices((list) =>
      list.map((i) => (i.id === id ? { ...i, amount: val } : i)),
    );

  const payApplied = payInvoices
    .filter((i) => i.include)
    .reduce((s, i) => s + (Number(i.amount) || 0), 0);

  const applyBulkPayment = async () => {
    const allocations = payInvoices
      .filter((i) => i.include && Number(i.amount) > 0)
      .map((i) => ({ invoice_id: i.id, amount: Number(i.amount) }));
    if (!allocations.length) {
      toast.error("Enter an amount to apply to at least one invoice");
      return;
    }
    setPaySaving(true);
    try {
      const { data } = await api.post("/invoices/bulk-payment", {
        allocations,
        method: payMethod,
        reference: payRef,
        at: payDate,
      });
      toast.success(
        `Applied ${money(data.applied)} across ${data.count} invoice${data.count > 1 ? "s" : ""}`,
      );
      setPayOpen(false);
      load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not apply that payment",
      );
    }
    setPaySaving(false);
  };

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Invoices" />
      <main className="relative z-10 pb-28 pt-6">
        <div
          className="rounded-lg border border-zinc-800 bg-zinc-900 p-5"
          data-testid="invoices-header-card"
        >
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-zinc-500">
                Money
              </div>
              <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
                Invoices
              </h1>
              <p className="mt-1 text-sm text-zinc-500">
                Deposit and balance invoices raise themselves when a quote is
                accepted — record cheques here, cards land from the portal
              </p>
            </div>
          </div>

          <div
            className="mt-5 flex flex-wrap items-center gap-2 border-t border-zinc-800 pt-4"
            data-testid="invoices-actions"
          >
            <Button
              data-testid="invoice-new-button"
              onClick={() => setOpen(true)}
              className="h-12 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
            >
              <Plus size={15} weight="bold" className="mr-2" /> New invoice
            </Button>
            <Button
              data-testid="invoice-bulk-pay-button"
              variant="outline"
              onClick={openBulkPay}
              className="h-12 border-zinc-700 text-xs font-bold uppercase tracking-widest text-zinc-200 hover:bg-zinc-800 hover:text-white"
            >
              <HandCoins size={16} weight="bold" className="mr-2" /> Apply payment
            </Button>
            <div className="hidden overflow-hidden rounded-md border border-zinc-800 sm:flex">
              {[
                ["table", Rows],
                ["cards", SquaresFour],
              ].map(([key, Icon]) => (
                <button
                  key={key}
                  type="button"
                  data-testid={`invoices-view-${key}`}
                  onClick={() => setLayout(key)}
                  title={`${key} view`}
                  className={`flex h-12 items-center gap-2 px-4 text-xs font-semibold uppercase tracking-widest transition-colors duration-150 ${
                    layout === key
                      ? "bg-indigo-500 text-white"
                      : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800"
                  }`}
                >
                  <Icon size={16} weight="bold" /> {key}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
          <Stat
            label="Outstanding"
            value={money(meta.outstanding)}
            testid="invoices-stat-outstanding"
          />
          <Stat
            label="Paid"
            value={money(meta.paid)}
            testid="invoices-stat-paid"
          />
          <Stat
            label="Invoices"
            value={rows.length}
            testid="invoices-stat-count"
          />
        </div>

        <FilterBar
          className="mt-6"
          testidPrefix="invoices"
          search={{
            value: q,
            onChange: setQ,
            placeholder: "Search code, account, address",
            testid: "invoice-search",
          }}
          sections={[
            {
              key: "status",
              label: "Status",
              value: filter,
              allValue: "All",
              onChange: setFilter,
              options: FILTERS.map((s) => [
                s,
                s,
                `invoice-filter-${s.toLowerCase().replace(/ /g, "-")}`,
              ]),
            },
            {
              key: "plan",
              label: "Plan",
              value: plan,
              allValue: "All",
              onChange: setPlan,
              options: ["All", "Full", "50/50", "Deposit", "Balance"].map((s) => [
                s,
                s === "50/50" ? "50 / 50" : s,
                `invoice-plan-filter-${s.toLowerCase().replace(/\//g, "-")}`,
              ]),
            },
          ]}
        />

        {rows.length === 0 && (
          <div
            className="mt-6 rounded-lg border border-dashed border-zinc-800 p-10 text-center"
            data-testid="invoices-empty"
          >
            <Receipt
              size={28}
              weight="bold"
              className="mx-auto text-zinc-700"
            />
            <p className="mt-3 text-sm text-zinc-400">
              {filter === "Overdue"
                ? "Nothing is overdue — every open invoice is still within its due date."
                : "No invoices yet. Accept a quote and the deposit plus balance invoices appear here."}
            </p>
          </div>
        )}

        {rows.length > 0 && layout === "table" && (
          <div
            className="mt-6 hidden overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900 lg:block"
            data-testid="invoices-table"
          >
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-800 text-[10px] uppercase tracking-widest text-zinc-500">
                  <th className="px-4 py-3 text-left">Invoice</th>
                  <th className="px-4 py-3 text-left">Account</th>
                  <th className="px-4 py-3 text-left">For</th>
                  <th className="px-4 py-3 text-left">
                    <button
                      type="button"
                      data-testid="invoice-sort-plan"
                      onClick={() =>
                        setPlanSort((s) => (s === "asc" ? "desc" : "asc"))
                      }
                      className="inline-flex items-center gap-1 uppercase tracking-widest transition-colors hover:text-zinc-300"
                    >
                      Plan
                      <ArrowsDownUp
                        size={11}
                        weight="bold"
                        className={planSort ? "text-indigo-400" : "text-zinc-600"}
                      />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left">Due</th>
                  <th className="px-4 py-3 text-right">Amount</th>
                  <th className="px-4 py-3 text-right">Balance</th>
                  {meta.qboEnabled && (
                    <th className="px-4 py-3 text-right">QuickBooks</th>
                  )}
                  <th className="px-4 py-3 text-right">Status</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((r) => (
                  <tr
                    key={r.id}
                    data-testid={`invoice-row-${r.id}`}
                    onClick={() => navigate(`/invoices/${r.id}`)}
                    className="cursor-pointer border-b border-zinc-800/70 last:border-0 hover:bg-zinc-800/40"
                  >
                    <td className="px-4 py-3">
                      <span className="font-mono-data text-indigo-300">
                        {r.code}
                      </span>
                      {r.quote_code && (
                        <span className="ml-2 text-[11px] text-zinc-600">
                          {r.quote_code}
                        </span>
                      )}
                    </td>
                    <td className="max-w-[14rem] truncate px-4 py-3">
                      {r.customer_name || "—"}
                    </td>
                    <td className="max-w-[18rem] truncate px-4 py-3 text-zinc-400">
                      {r.title}
                    </td>
                    <td className="px-4 py-3">
                      {planPill(r.kind) ? (
                        <span
                          data-testid={`invoice-plan-${r.id}`}
                          className={`rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest ${planPill(r.kind).cls}`}
                        >
                          {planPill(r.kind).label}
                        </span>
                      ) : (
                        <span className="text-zinc-600">—</span>
                      )}
                    </td>
                    <td
                      className={`px-4 py-3 ${isOverdue(r) ? "text-red-300" : "text-zinc-400"}`}
                    >
                      {isOverdue(r) && <OverdueDot id={r.id} />}
                      {day(r.due_date)}
                    </td>
                    <td className="font-mono-data px-4 py-3 text-right">
                      {money(r.amount)}
                    </td>
                    <td className="font-mono-data px-4 py-3 text-right font-bold">
                      {money(r.balance)}
                    </td>
                    {meta.qboEnabled && (
                      <td className="px-4 py-3 text-right">
                        <span
                          data-testid={`invoice-qbo-${r.id}`}
                          className={`whitespace-nowrap rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest ${qboTag(r.qbo).cls}`}
                        >
                          {qboTag(r.qbo).label}
                        </span>
                      </td>
                    )}
                    <td className="px-4 py-3 text-right">
                      <span
                        className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest ${
                          INVOICE_BADGE[r.status] || INVOICE_BADGE.Draft
                        }`}
                      >
                        {r.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {rows.length > 0 && (
          <div
            className={`mt-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-3 ${
              layout === "table" ? "lg:hidden" : ""
            }`}
            data-testid="invoices-cards"
          >
            {paged.map((r) => (
              <button
                key={r.id}
                type="button"
                data-testid={`invoice-card-${r.id}`}
                onClick={() => navigate(`/invoices/${r.id}`)}
                className="rounded-lg border border-zinc-800 bg-zinc-900 p-4 text-left transition-colors duration-150 hover:border-zinc-700"
              >
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-mono-data text-xs text-indigo-300">
                      {r.code}
                      {r.quote_code && (
                        <span className="ml-2 text-zinc-600">
                          {r.quote_code}
                        </span>
                      )}
                    </div>
                    {planPill(r.kind) && (
                      <span
                        data-testid={`invoice-plan-card-${r.id}`}
                        className={`mt-1 inline-block rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest ${planPill(r.kind).cls}`}
                      >
                        {planPill(r.kind).label}
                      </span>
                    )}
                    <h3 className="truncate text-base font-bold">
                      {r.customer_name || "—"}
                    </h3>
                    <p className="truncate text-xs text-zinc-500">{r.title}</p>
                  </div>
                  <span
                    className={`shrink-0 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest ${
                      INVOICE_BADGE[r.status] || INVOICE_BADGE.Draft
                    }`}
                  >
                    {r.status}
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-zinc-800 pt-3 text-[11px] uppercase tracking-widest text-zinc-500">
                  <span className={isOverdue(r) ? "text-red-300" : ""}>
                    {isOverdue(r) && <OverdueDot id={`card-${r.id}`} />}
                    due {day(r.due_date)}
                  </span>
                  {meta.qboEnabled && (
                    <span
                      data-testid={`invoice-qbo-card-${r.id}`}
                      className={`rounded-full border px-2 py-0.5 text-[9px] normal-case tracking-normal ${qboTag(r.qbo).cls}`}
                    >
                      QB: {qboTag(r.qbo).label}
                    </span>
                  )}
                  <span>
                    <span className="font-mono-data text-zinc-300">
                      {money(r.amount)}
                    </span>{" "}
                    total
                  </span>
                  <span className="font-mono-data ml-auto text-base font-bold text-white">
                    {money(r.balance)}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
        <Pagination page={curPage} total={displayRows.length} onPage={setPage} testId="invoices-pagination" />
      </main>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="border-zinc-800 bg-zinc-900"
          data-testid="invoice-new-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Receipt size={17} weight="bold" className="text-indigo-300" />{" "}
              New invoice
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                Account
              </span>
              <Select
                value={form.customer_id}
                onValueChange={(v) => setForm({ ...form, customer_id: v })}
              >
                <SelectTrigger
                  data-testid="invoice-new-customer"
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                >
                  <SelectValue placeholder="Pick an account" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                What is it for
              </span>
              <Input
                data-testid="invoice-new-title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Extra sink cut-out"
                className="mt-1 h-11 border-zinc-800 bg-zinc-950"
              />
            </label>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block">
                <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                  Amount
                </span>
                <Input
                  data-testid="invoice-new-amount"
                  type="number"
                  step="0.01"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </label>
              <label className="block">
                <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                  Due date
                </span>
                <DateField
                  testid="invoice-new-due"
                  value={form.due_date}
                  placeholder="Pick a date"
                  className="mt-1"
                  onChange={(v) => setForm({ ...form, due_date: v })}
                />
              </label>
            </div>
            <Button
              data-testid="invoice-new-save"
              onClick={create}
              className="h-11 w-full bg-indigo-500 text-[11px] font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
            >
              Create invoice
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent
          className="max-h-[90vh] overflow-y-auto border-zinc-800 bg-zinc-900 sm:max-w-2xl"
          data-testid="invoice-bulk-pay-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <HandCoins size={17} weight="bold" className="text-emerald-300" />{" "}
              Apply payment to multiple invoices
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                Account
              </span>
              <Select value={payCust} onValueChange={pickPayCustomer}>
                <SelectTrigger
                  data-testid="invoice-bulk-pay-customer"
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                >
                  <SelectValue placeholder="Pick an account" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>

            {payCust && payInvoices.length === 0 && (
              <p
                className="rounded-lg border border-dashed border-zinc-800 p-4 text-center text-sm text-zinc-500"
                data-testid="invoice-bulk-pay-none"
              >
                This account has no open invoices to pay.
              </p>
            )}

            {payInvoices.length > 0 && (
              <>
                <div className="grid gap-3 sm:grid-cols-3">
                  <label className="block sm:col-span-1">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                      Total received
                    </span>
                    <Input
                      data-testid="invoice-bulk-pay-total"
                      type="number"
                      step="0.01"
                      value={payTotal}
                      onChange={(e) => setTotalAndAllocate(e.target.value)}
                      placeholder="0.00"
                      className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                      Method
                    </span>
                    <Select value={payMethod} onValueChange={setPayMethod}>
                      <SelectTrigger
                        data-testid="invoice-bulk-pay-method"
                        className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PAY_METHODS.map((m) => (
                          <SelectItem key={m} value={m}>
                            {m}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </label>
                  <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                      Date
                    </span>
                    <DateField
                      testid="invoice-bulk-pay-date"
                      value={payDate}
                      placeholder="Today"
                      className="mt-1"
                      onChange={setPayDate}
                    />
                  </label>
                </div>
                <label className="block">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                    Reference (cheque #, confirmation…)
                  </span>
                  <Input
                    data-testid="invoice-bulk-pay-reference"
                    value={payRef}
                    onChange={(e) => setPayRef(e.target.value)}
                    placeholder="Optional"
                    className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                  />
                </label>

                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                    Split
                  </span>
                  <div
                    className="flex overflow-hidden rounded-md border border-zinc-800"
                    data-testid="invoice-bulk-pay-split"
                  >
                    {[
                      ["oldest", "Oldest first"],
                      ["even", "Split evenly"],
                    ].map(([k, label]) => (
                      <button
                        key={k}
                        type="button"
                        data-testid={`invoice-bulk-pay-split-${k}`}
                        onClick={() => setSplitMode(k)}
                        className={`px-3 py-1.5 text-xs font-semibold transition-colors duration-150 ${
                          paySplit === k
                            ? "bg-emerald-500/20 text-emerald-200"
                            : "text-zinc-400 hover:bg-zinc-800"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="overflow-hidden rounded-lg border border-zinc-800">
                  <table className="w-full text-sm" data-testid="invoice-bulk-pay-table">
                    <thead>
                      <tr className="border-b border-zinc-800 text-[10px] uppercase tracking-widest text-zinc-500">
                        <th className="px-3 py-2 text-left">Apply</th>
                        <th className="px-3 py-2 text-left">Invoice</th>
                        <th className="px-3 py-2 text-right">Balance</th>
                        <th className="px-3 py-2 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payInvoices.map((inv) => (
                        <tr
                          key={inv.id}
                          data-testid={`invoice-bulk-pay-row-${inv.id}`}
                          className="border-b border-zinc-800/70 last:border-0"
                        >
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              data-testid={`invoice-bulk-pay-include-${inv.id}`}
                              checked={inv.include}
                              onChange={() => toggleInclude(inv.id)}
                              className="h-4 w-4 accent-emerald-500"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <span className="font-mono-data text-indigo-300">
                              {inv.code}
                            </span>
                            <span className="ml-2 text-[11px] text-zinc-500">
                              due {day(inv.due_date)}
                            </span>
                          </td>
                          <td className="font-mono-data px-3 py-2 text-right text-zinc-400">
                            {money(inv.balance)}
                          </td>
                          <td className="px-3 py-2 text-right">
                            <Input
                              data-testid={`invoice-bulk-pay-amount-${inv.id}`}
                              type="number"
                              step="0.01"
                              disabled={!inv.include}
                              value={inv.amount}
                              onChange={(e) => setRowAmount(inv.id, e.target.value)}
                              placeholder="0.00"
                              className="ml-auto h-9 w-28 border-zinc-800 bg-zinc-950 text-right disabled:opacity-40"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-400">Applying</span>
                  <span
                    className="font-mono-data text-lg font-bold text-emerald-300"
                    data-testid="invoice-bulk-pay-applied"
                  >
                    {money(payApplied)}
                  </span>
                </div>
                <Button
                  data-testid="invoice-bulk-pay-save"
                  onClick={applyBulkPayment}
                  disabled={paySaving || payApplied <= 0}
                  className="h-11 w-full bg-emerald-500 text-[11px] font-bold uppercase tracking-widest text-white hover:bg-emerald-400 disabled:opacity-40"
                >
                  {paySaving ? "Applying…" : `Apply ${money(payApplied)}`}
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
